import { Button, Chip } from '@sjuni/ui';
import { FileText } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DocumentLibraryDialog } from '@/components/documents/DocumentLibraryDialog';
import { useEditorLocale } from '@/lib/editorLocale/useEditorLocale';
import type { TranslatedValue } from '@/lib/forms/translatable';
import { hasContent, valueFor } from '@/lib/forms/translatable';

/**
 * A field that attaches a document from the library.
 *
 * The counterpart of `MediaPicker`, and for the same reason. A modal's call to
 * action could point at a document, and the way to say which was to type its
 * database id into a number box: an editor had to open the documents screen,
 * find the regulation, read an id out of a URL, and type it back — with nothing
 * checking they had read the right one, and nothing afterwards showing which
 * document a modal was offering. A wrong digit produced a modal whose button
 * downloaded somebody else's file.
 *
 * `value` is the attached document as the resource sends it — `{id, title}` —
 * rather than a `DocumentRow`, because that is what an editor form has to hand
 * when it loads a record. Everything an editor picks in the dialog is richer than
 * that, so the label is narrowed to the shape both can satisfy.
 */
export interface DocumentPickerProps {
  label: string;
  hint?: string;
  /** The currently attached document, or null. */
  value: { id: number; title: TranslatedValue } | null;
  /** Called with the new document, or null when it is detached. */
  onChange: (document: { id: number; title: TranslatedValue } | null) => void;
  error?: string;
  disabled?: boolean;
}

export function DocumentPicker({
  label,
  hint,
  value,
  onChange,
  error,
  disabled,
}: DocumentPickerProps) {
  const { t } = useTranslation();
  const { locale } = useEditorLocale();
  const [open, setOpen] = useState(false);

  /*
   * The Georgian title when the editor is working in English and there is no
   * English one — the same direction as every other translatable field
   * (CLAUDE.md rule 5). A blank chip would read as "nothing is attached", which
   * is the one thing it must not say.
   *
   * `hasContent` rather than `??`, because `valueFor` answers a missing
   * translation with `''` and not with null: a nullish fallback never fires and
   * the chip goes blank on exactly the 1,714 documents that have no English title.
   */
  const title =
    value === null ? null : valueFor(value.title, hasContent(value.title, locale) ? locale : 'ka');

  return (
    <div>
      <span className="mb-1.5 block text-body-sm font-semibold text-primary">{label}</span>
      {hint ? <span className="mb-1.5 block text-caption text-muted">{hint}</span> : null}

      <div className="flex flex-wrap items-center gap-2">
        {value ? (
          <Chip>
            <FileText size={14} strokeWidth={1.75} aria-hidden="true" />
            {/* The title, never the id. An editor should not have to know one. */}
            {title === null || title.trim() === '' ? t('documents.picker.untitled') : title}
          </Chip>
        ) : (
          <span className="text-body-sm text-muted">{t('documents.picker.none')}</span>
        )}

        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={disabled}
          onClick={() => setOpen(true)}
        >
          {value ? t('documents.picker.change') : t('documents.picker.choose')}
        </Button>

        {value ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={disabled}
            onClick={() => onChange(null)}
          >
            {t('documents.picker.remove')}
          </Button>
        ) : null}
      </div>

      {error ? <p className="text-danger-600 mt-1.5 text-caption">{error}</p> : null}

      {open ? (
        <DocumentLibraryDialog
          onClose={() => setOpen(false)}
          onPick={(document) => {
            onChange({ id: document.id, title: document.title });
            setOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}
